import { buildRealtyFilterWhere } from '../api/realty/realty-api';
import type { FetchRealtyQuery } from '../api/realty/realty-api';
import {
  buildRealtyWhere,
  type RealtyNumericInput,
} from '@eliadmin/core/src/api/realty/realty-list-request';
import { normalizeRealtyFilter, parseRealtyFilterNumber } from './realty-filter-contract';
import type { RealtyFilter } from './realty-filter-contract';

type BuildRealtyQueryOptions = {
  type?: (string | number)[];
  offer_type?: (string | number)[];
  priceFrom?: RealtyNumericInput;
  priceTo?: RealtyNumericInput;
  areaFrom?: RealtyNumericInput;
  areaTo?: RealtyNumericInput;
  city?: string | number;
  village?: string | number;
  numberOfRooms?: number[];
  isNew?: boolean;
  supportsUnderConstruction?: boolean;
  search?: string;
};

export const buildRealtyQuery = (query: BuildRealtyQueryOptions): FetchRealtyQuery => {
  const normalized = normalizeRealtyFilter({
    offer_type: query.offer_type?.map(Number),
    price_from: parseRealtyFilterNumber(query.priceFrom),
    price_to: parseRealtyFilterNumber(query.priceTo),
    area_from: parseRealtyFilterNumber(query.areaFrom),
    area_to: parseRealtyFilterNumber(query.areaTo),
    city: query.city?.toString(),
    village: query.village?.toString(),
    number_of_rooms: query.numberOfRooms,
    is_new: query.isNew,
    search: query.search,
  });

  const selectedWhere = buildRealtyFilterWhere({
    type_id: query.type,
    offer_type_id: normalized.offer_type,
    city_id: normalized.city,
    village_id: normalized.village,
    number_of_rooms: normalized.number_of_rooms,
    under_construction: query.supportsUnderConstruction === true ? normalized.is_new : undefined,
  });
  const where = {
    ...selectedWhere,
    ...buildRealtyWhere({
      priceFrom: normalized.price_from,
      priceTo: normalized.price_to,
      areaFrom: normalized.area_from,
      areaTo: normalized.area_to,
    }),
  };

  return {
    where: Object.keys(where).length > 0 ? where : undefined,
    search: normalized.search,
  };
};

/** One public request contract for SSR first page and browser pagination. */
export function buildRealtyListingQuery(
  filter: RealtyFilter,
  page = 1,
  supportsUnderConstruction = false,
): FetchRealtyQuery {
  const sort: Pick<FetchRealtyQuery, 'order' | 'orderBy' | 'order[]' | 'orderBy[]'> =
    filter.orderBy === 'price'
      ? { 'orderBy[]': ['price', 'id'], 'order[]': [filter.order ?? 'desc', 'asc'] }
      : { orderBy: 'id', order: filter.order ?? 'desc' };
  return {
    page,
    results: 5,
    images: true,
    ...sort,
    ...buildRealtyQuery({
      type: filter.type ? [filter.type] : undefined,
      offer_type: filter.offer_type,
      priceFrom: filter.price_from,
      priceTo: filter.price_to,
      areaFrom: filter.area_from,
      areaTo: filter.area_to,
      city: filter.city,
      village: filter.village,
      numberOfRooms: filter.number_of_rooms,
      isNew: filter.is_new,
      supportsUnderConstruction,
      search: filter.search,
    }),
  };
}
